import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "Shared/util/custom-hooks";
import { activateOpenBankingRedirectConsent } from "Shared/util/web-api";
import {
    AuthenticationFlowResource,
    ConsentResource,
    PaymentInformationMessageResource,
} from "Shared/util/types";
import FadeTransition from "Shared/components/transitions/fade-transition";
import Button from "Shared/components/button";
import Spinner from "Shared/components/spinner";
import { OpenBankingPrivacyPolicy } from "../components";
import { useConsentCreater, useConsentStatusPoller } from "../hooks";
import styles from "./redirect-tab.css";
import { RedirectTabProps } from "./types";

export const RedirectTab = ({
    data: {
        bic,
        callbackActivationCode,
        callbackToken,
        finaBankId,
        isCallback,
        socialSecurityNumber,
    },
    onError,
    onUserVerify,
    show,
}: RedirectTabProps) => {
    const t = useTranslation((x) => x.edit_payment_information);
    const { redirect_tab: ttab } = t;

    const [isLoading, setIsLoading] = useState(false);

    const handleConsentCreated = useCallback(
        (_: AuthenticationFlowResource, { challengeData }: ConsentResource) =>
            (window.location.href = challengeData),
        [],
    );

    const handleConsentError = useCallback(() => onError(true), [onError]);

    const consentCreater = useConsentCreater(
        handleConsentCreated,
        handleConsentError,
    );

    const handlePollFailedStatus = useCallback(
        (message: PaymentInformationMessageResource | null) =>
            onError(true, message),
        [onError],
    );

    const handlePollFetchError = useCallback(
        (message: PaymentInformationMessageResource | null) =>
            onError(true, message),
        [onError],
    );

    const { begin: beginConsentPoller } = useConsentStatusPoller(
        onUserVerify,
        handlePollFailedStatus,
        handlePollFetchError,
    );

    useEffect(() => {
        if (show && isCallback && callbackToken) {
            activateOpenBankingRedirectConsent(
                callbackActivationCode,
                callbackToken,
            )
                .then(() => beginConsentPoller(callbackToken))
                .catch(() => onError(true));
        }
    }, [beginConsentPoller, callbackToken, isCallback, show]);

    const handleBankRedirect = () => {
        setIsLoading(true);
        consentCreater(
            AuthenticationFlowResource.Redirect,
            finaBankId,
            bic,
            socialSecurityNumber,
        );
    };

    return (
        <FadeTransition
            className={styles.container}
            enterDuration={200}
            isOpen={show}
        >
            {!show ? null : (
                <>
                    <h3>{ttab.identify_via_bank}</h3>
                    {isCallback ? (
                        <>
                            <Spinner customClass={styles.spinner} />
                            <p className={styles.waitingForIdentification}>
                                {t.waiting_for_identification}
                            </p>
                        </>
                    ) : (
                        <>
                            <p>{ttab.redirect_flow_description}</p>
                            <hr className={styles.divider} />
                            <OpenBankingPrivacyPolicy />
                            <Button
                                buttonType="action-primary"
                                className={styles.redirectButton}
                                isLoading={isLoading}
                                onClick={handleBankRedirect}
                                text={ttab.continue_to_bank}
                            />
                        </>
                    )}
                </>
            )}
        </FadeTransition>
    );
};
